// src/components/trades/mockups/TradeProposalMockupC.js
// OPTION C: Center Trade Bar Layout

import React, { useState } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { ArrowRightLeft, X, DollarSign, Plus } from 'lucide-react';

/**
 * MOCKUP C: Center Trade Bar Layout
 * - Both rosters visible in columns
 * - Central "staging area" showing selected assets
 * - Click to add, click X to remove
 * - Visual trade flow
 */
const TradeProposalMockupC = () => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [mySelectedPlayers, setMySelectedPlayers] = useState([]);
  const [theirSelectedPlayers, setTheirSelectedPlayers] = useState([]);
  const [mySelectedPicks, setMySelectedPicks] = useState([]);
  const [theirSelectedPicks, setTheirSelectedPicks] = useState([]);

  // Mock data
  const mockTeams = [
    { team_id: '1', team_name: 'Thunder Bats' },
    { team_id: '2', team_name: 'Dynasty Kings' },
    { team_id: '3', team_name: 'Home Run Heroes' }
  ];

  const mockMyRoster = [
    {
      id: 1,
      name: 'Juan Soto',
      mlb_team: 'NYY',
      position: 'OF',
      games: 145,
      ab: 550,
      hits: 165,
      avg: '.300',
      hr: 41,
      rbi: 109,
      sb: 7,
      price: 65,
      salary: 62,
      contract: 3,
      rolling_avg: '.312',
      rolling_hr: 8,
      rolling_rbi: 21
    },
    {
      id: 2,
      name: 'Ronald Acuña Jr.',
      mlb_team: 'ATL',
      position: 'OF',
      games: 119,
      ab: 455,
      hits: 149,
      avg: '.327',
      hr: 29,
      rbi: 85,
      sb: 43,
      price: 72,
      salary: 68,
      contract: 4,
      rolling_avg: '.298',
      rolling_hr: 4,
      rolling_rbi: 12
    },
    {
      id: 3,
      name: 'Gerrit Cole',
      mlb_team: 'NYY',
      position: 'SP',
      games: 30,
      starts: 30,
      wins: 14,
      qs: 19,
      k: 209,
      saves: 0,
      era: '2.87',
      whip: '1.02',
      price: 58,
      salary: 55,
      contract: 2,
      rolling_era: '2.45',
      rolling_k: 42,
      rolling_qs: 4
    }
  ];

  const mockTheirRoster = [
    {
      id: 4,
      name: 'Mookie Betts',
      mlb_team: 'LAD',
      position: 'OF',
      games: 152,
      ab: 575,
      hits: 179,
      avg: '.311',
      hr: 35,
      rbi: 97,
      sb: 12,
      price: 68,
      salary: 65,
      contract: 3,
      rolling_avg: '.329',
      rolling_hr: 7,
      rolling_rbi: 18
    },
    {
      id: 5,
      name: 'Corbin Burnes',
      mlb_team: 'BAL',
      position: 'SP',
      games: 32,
      starts: 32,
      wins: 15,
      qs: 22,
      k: 181,
      saves: 0,
      era: '3.19',
      whip: '1.09',
      price: 52,
      salary: 48,
      contract: 2,
      rolling_era: '2.98',
      rolling_k: 38,
      rolling_qs: 5
    }
  ];

  const mockMyPicks = [
    { pick_id: '1', year: 2026, round: 1, overall: 3 },
    { pick_id: '2', year: 2026, round: 3, overall: 27 },
    { pick_id: '3', year: 2027, round: 1, overall: 5 }
  ];

  const mockTheirPicks = [
    { pick_id: '4', year: 2026, round: 2, overall: 14 },
    { pick_id: '5', year: 2027, round: 1, overall: 2 },
    { pick_id: '6', year: 2027, round: 2, overall: 18 }
  ];

  const togglePlayerSelection = (playerId, isMine) => {
    if (isMine) {
      setMySelectedPlayers(prev => 
        prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
      );
    } else {
      setTheirSelectedPlayers(prev => 
        prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
      );
    }
  };

  const togglePickSelection = (pickId, isMine) => {
    if (isMine) {
      setMySelectedPicks(prev => 
        prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
      );
    } else {
      setTheirSelectedPicks(prev => 
        prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
      );
    }
  };

  // Get selected asset objects
  const mySelectedPlayerObjects = mockMyRoster.filter(p => mySelectedPlayers.includes(p.id));
  const theirSelectedPlayerObjects = mockTheirRoster.filter(p => theirSelectedPlayers.includes(p.id));
  const mySelectedPickObjects = mockMyPicks.filter(p => mySelectedPicks.includes(p.pick_id));
  const theirSelectedPickObjects = mockTheirPicks.filter(p => theirSelectedPicks.includes(p.pick_id));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
          OPTION C: Center Trade Bar
        </h4>
        <div className={`px-3 py-1 ${dynastyTheme.components.badge.warning} text-xs`}>
          Hybrid Layout
        </div>
      </div>

      {/* Team Selector */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
          Select Trading Partner
        </label>
        <select
          value={selectedTeam || ''}
          onChange={(e) => setSelectedTeam(e.target.value)}
          className={`w-full md:w-64 px-4 py-2 ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} rounded-lg`}
        >
          <option value="">Choose a team...</option>
          {mockTeams.map(team => (
            <option key={team.team_id} value={team.team_id}>
              {team.team_name}
            </option>
          ))}
        </select>
      </div>

      {/* Three-Column Layout */}
      {selectedTeam && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT COLUMN - Your Roster (Available Assets) */}
          <div className="lg:col-span-4">
            <div className={`${dynastyTheme.components.card.base} p-4 space-y-3`}>
              <h5 className={`text-sm font-bold ${dynastyTheme.classes.text.primary}`}>
                YOUR ROSTER
              </h5>
              
              {/* Compact Player List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {mockMyRoster.map(player => (
                  <CompactPlayerCard
                    key={player.id}
                    player={player}
                    selected={mySelectedPlayers.includes(player.id)}
                    onToggle={() => togglePlayerSelection(player.id, true)}
                  />
                ))}
              </div>

              {/* Compact Pick List */}
              <div className="pt-2 border-t border-neutral-700">
                <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                  DRAFT PICKS
                </div>
                <div className="space-y-1">
                  {mockMyPicks.map(pick => (
                    <CompactPickCard
                      key={pick.pick_id}
                      pick={pick}
                      selected={mySelectedPicks.includes(pick.pick_id)}
                      onToggle={() => togglePickSelection(pick.pick_id, true)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CENTER COLUMN - Trade Staging Area */}
          <div className="lg:col-span-4">
            <div className={`${dynastyTheme.components.card.base} p-4 space-y-4 min-h-96`}>
              {/* Your Side */}
              <div>
                <div className={`flex items-center justify-between mb-2`}>
                  <h5 className={`text-sm font-bold ${dynastyTheme.classes.text.primary}`}>
                    YOU GIVE
                  </h5>
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    {mySelectedPlayers.length + mySelectedPicks.length} items
                  </span>
                </div>
                
                <div className={`p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg border ${dynastyTheme.classes.border.primary} min-h-32`}>
                  {mySelectedPlayerObjects.length === 0 && mySelectedPickObjects.length === 0 ? (
                    <div className={`text-center py-6 ${dynastyTheme.classes.text.neutralLighter} text-sm`}>
                      Click players/picks to add
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {/* Selected Players */}
                      {mySelectedPlayerObjects.map(player => (
                        <SelectedAssetChip
                          key={player.id}
                          label={`${player.name} ($${player.salary})`}
                          onRemove={() => togglePlayerSelection(player.id, true)}
                        />
                      ))}
                      {/* Selected Picks */}
                      {mySelectedPickObjects.map(pick => (
                        <SelectedAssetChip
                          key={pick.pick_id}
                          label={`${pick.year} Rd${pick.round}`}
                          onRemove={() => togglePickSelection(pick.pick_id, true)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Cash Input */}
                <div className="mt-2">
                  <div className={`flex items-center gap-2 p-2 ${dynastyTheme.classes.bg.darkLighter} rounded border border-dashed border-neutral-600`}>
                    <DollarSign className="w-4 h-4 text-neutral-500" />
                    <input
                      type="number"
                      placeholder="Cash"
                      disabled
                      className={`flex-1 bg-transparent ${dynastyTheme.classes.text.neutralLight} text-xs`}
                    />
                  </div>
                </div>
              </div>

              {/* Trade Arrow */}
              <div className="flex items-center justify-center py-2">
                <div className={`p-2 ${dynastyTheme.classes.bg.primary} rounded-full`}>
                  <ArrowRightLeft className={`w-5 h-5 ${dynastyTheme.classes.text.dark}`} />
                </div>
              </div>

              {/* Their Side */}
              <div>
                <div className={`flex items-center justify-between mb-2`}>
                  <h5 className={`text-sm font-bold ${dynastyTheme.classes.text.warning}`}>
                    YOU GET
                  </h5>
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    {theirSelectedPlayers.length + theirSelectedPicks.length} items
                  </span>
                </div>
                
                <div className={`p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg border ${dynastyTheme.classes.border.warning} min-h-32`}>
                  {theirSelectedPlayerObjects.length === 0 && theirSelectedPickObjects.length === 0 ? (
                    <div className={`text-center py-6 ${dynastyTheme.classes.text.neutralLighter} text-sm`}>
                      Click players/picks to add
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {/* Selected Players */}
                      {theirSelectedPlayerObjects.map(player => (
                        <SelectedAssetChip
                          key={player.id}
                          label={`${player.name} ($${player.salary})`}
                          onRemove={() => togglePlayerSelection(player.id, false)}
                          variant="warning"
                        />
                      ))}
                      {/* Selected Picks */}
                      {theirSelectedPickObjects.map(pick => (
                        <SelectedAssetChip
                          key={pick.pick_id}
                          label={`${pick.year} Rd${pick.round}`}
                          onRemove={() => togglePickSelection(pick.pick_id, false)}
                          variant="warning"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Cash Input */}
                <div className="mt-2">
                  <div className={`flex items-center gap-2 p-2 ${dynastyTheme.classes.bg.darkLighter} rounded border border-dashed border-neutral-600`}>
                    <DollarSign className="w-4 h-4 text-neutral-500" />
                    <input
                      type="number"
                      placeholder="Cash"
                      disabled
                      className={`flex-1 bg-transparent ${dynastyTheme.classes.text.neutralLight} text-xs`}
                    />
                  </div>
                </div>
              </div>

              {/* Notes & Submit */}
              <div className="pt-3 border-t border-neutral-700">
                <textarea
                  placeholder="Trade notes..."
                  rows={2}
                  className={`w-full px-3 py-2 mb-2 ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} rounded-lg text-xs`}
                />
                <button
                  disabled={
                    (mySelectedPlayers.length + mySelectedPicks.length === 0) &&
                    (theirSelectedPlayers.length + theirSelectedPicks.length === 0)
                  }
                  className={`w-full ${dynastyTheme.utils.getComponent('button', 'primary', 'sm')}`}
                >
                  Propose Trade
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Their Roster (Available Assets) */}
          <div className="lg:col-span-4">
            <div className={`${dynastyTheme.components.card.base} p-4 space-y-3`}>
              <h5 className={`text-sm font-bold ${dynastyTheme.classes.text.warning}`}>
                THEIR ROSTER - {mockTeams.find(t => t.team_id === selectedTeam)?.team_name}
              </h5>
              
              {/* Compact Player List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {mockTheirRoster.map(player => (
                  <CompactPlayerCard
                    key={player.id}
                    player={player}
                    selected={theirSelectedPlayers.includes(player.id)}
                    onToggle={() => togglePlayerSelection(player.id, false)}
                  />
                ))}
              </div>

              {/* Compact Pick List */}
              <div className="pt-2 border-t border-neutral-700">
                <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                  DRAFT PICKS
                </div>
                <div className="space-y-1">
                  {mockTheirPicks.map(pick => (
                    <CompactPickCard
                      key={pick.pick_id}
                      pick={pick}
                      selected={theirSelectedPicks.includes(pick.pick_id)}
                      onToggle={() => togglePickSelection(pick.pick_id, true)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact Player Card (for side columns)
const CompactPlayerCard = ({ player, selected, onToggle }) => {
  return (
    <div
      onClick={onToggle}
      className={`
        p-2 rounded border cursor-pointer transition-all
        ${selected 
          ? `${dynastyTheme.classes.border.primary} bg-yellow-400/10`
          : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
        }
        hover:border-yellow-400/50
      `}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
            {player.name}
          </div>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {player.position} • ${player.salary}
          </div>
        </div>
        {selected && (
          <div className={`w-5 h-5 rounded-full ${dynastyTheme.classes.bg.primary} flex items-center justify-center`}>
            <Plus className={`w-3 h-3 ${dynastyTheme.classes.text.dark}`} />
          </div>
        )}
      </div>
    </div>
  );
};

// Compact Pick Card (for side columns)
const CompactPickCard = ({ pick, selected, onToggle }) => {
  return (
    <div
      onClick={onToggle}
      className={`
        p-1.5 rounded border cursor-pointer transition-all text-xs
        ${selected 
          ? `${dynastyTheme.classes.border.primary} bg-yellow-400/10`
          : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
        }
        hover:border-yellow-400/50
      `}
    >
      <div className="flex items-center justify-between">
        <span className={dynastyTheme.classes.text.white}>
          {pick.year} Rd{pick.round}
        </span>
        {selected && (
          <div className={`w-4 h-4 rounded-full ${dynastyTheme.classes.bg.primary} flex items-center justify-center`}>
            <Plus className={`w-2.5 h-2.5 ${dynastyTheme.classes.text.dark}`} />
          </div>
        )}
      </div>
    </div>
  );
};

// Selected Asset Chip (for center staging area)
const SelectedAssetChip = ({ label, onRemove, variant = 'primary' }) => {
  return (
    <div className={`
      flex items-center justify-between p-2 rounded
      ${variant === 'warning' 
        ? 'bg-orange-400/20 border border-orange-400/30' 
        : 'bg-yellow-400/20 border border-yellow-400/30'
      }
    `}>
      <span className={`text-xs font-medium ${dynastyTheme.classes.text.white}`}>
        {label}
      </span>
      <button
        onClick={onRemove}
        className={`ml-2 hover:bg-red-500/20 rounded-full p-0.5 transition-colors`}
      >
        <X className={`w-3 h-3 ${dynastyTheme.classes.text.neutralLight}`} />
      </button>
    </div>
  );
};

export default TradeProposalMockupC;
